import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export interface Toast {
  id: number
  title: string
  description?: string
  /** In-app link (e.g. the ticket a mention came from). */
  href?: string
  tone?: 'info' | 'warning' | 'error'
}

interface ToastContextValue {
  toasts: Toast[]
  notify: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

const TTL_MS = 8_000

/** Minimal in-app toasts (mentions, SLA warnings, concurrency notices). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), [])

  const notify = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++
      setToasts((all) => [...all.slice(-3), { ...toast, id }])
      window.setTimeout(() => dismiss(id), TTL_MS)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toasts, notify, dismiss }), [toasts, notify, dismiss])
  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

function Toaster() {
  const { t } = useTranslation()
  const { toasts, dismiss } = useToast()
  if (toasts.length === 0) return null
  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-[60] flex w-80 flex-col gap-2" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto flex items-start gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg',
            toast.tone === 'error' ? 'border-status-escalated-fg/40' : toast.tone === 'warning' ? 'border-note-border' : 'border-line-strong',
          )}
        >
          <div className="flex min-w-0 grow flex-col gap-0.5">
            {toast.href ? (
              <Link to={toast.href} onClick={() => dismiss(toast.id)} className="text-meta font-semibold text-ink hover:underline">
                {toast.title}
              </Link>
            ) : (
              <p className="text-meta font-semibold text-ink">{toast.title}</p>
            )}
            {toast.description ? <p className="text-label text-ink-muted">{toast.description}</p> : null}
          </div>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label={t('common.dismiss')}
            className="shrink-0 rounded p-0.5 text-ink-muted hover:text-ink"
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}
